import React from "react";
import DeviceInterface from "../../interfaces/DeviceInterface";
import GroupInterface from "../../interfaces/GroupInterface";
import useDataStore from "./useDataStore";

interface DeviceGroupStoreContextInterface {
  deviceStore: DeviceInterface[];
  groupStore: GroupInterface[];
  saveDeviceGroup: Function;
  saveGroup: Function;
  removeGroup: Function;
  findGroupById: Function;
  findDeviceById: Function;
  findDeviceListByGroupId: Function;
}

const DeviceGroupStoreContext = React.createContext<any>(undefined);

function useDeviceGroupStore() {
  return React.useContext<DeviceGroupStoreContextInterface>(
    DeviceGroupStoreContext
  );
}

function DeviceGroupStore({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) {
  const { deviceStore, groupStore, saveDeviceGroup, saveGroup, removeGroup } =
    useDataStore();

  const findDeviceById = React.useCallback(
    (deviceId: string, deviceType: string): DeviceInterface | undefined =>
      deviceStore.find((d) => d.id === deviceId && d.type === deviceType),
    [deviceStore]
  );

  const findGroupById = React.useCallback(
    (groupId: string): GroupInterface | undefined =>
      groupStore.find((g) => g.id === groupId),
    [groupStore]
  );

  const findDeviceListByGroupId = React.useCallback(
    (groupId: string | undefined) =>
      deviceStore.filter((d) => d.groupId === groupId),
    [deviceStore]
  );

  const value = React.useMemo(
    () => ({
      deviceStore,
      groupStore,
      saveDeviceGroup,
      saveGroup,
      removeGroup,
      findGroupById,
      findDeviceById,
      findDeviceListByGroupId,
    }),
    [
      deviceStore,
      findDeviceById,
      findDeviceListByGroupId,
      findGroupById,
      groupStore,
      removeGroup,
      saveDeviceGroup,
      saveGroup,
    ]
  );

  return (
    <DeviceGroupStoreContext.Provider value={value}>
      {children}
    </DeviceGroupStoreContext.Provider>
  );
}

export { DeviceGroupStore as default, useDeviceGroupStore };
